/**
 * PlanListRow Component
 *
 * Displays a plan in the plan list with per-plan access level information.
 * Shows the viewer's specific access level for each plan (Owner/Admin/Participant).
 *
 * Authorization note: Access is per-(planId, personId) scoped.
 * A person who is ADMIN in Plan A may be ROLE_MEMBER in Plan B.
 */

import React from 'react';
import { Box, Typography, Chip, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PlanStatus, ParticipantAccessLevel } from '../types/generated/graphql';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupIcon from '@mui/icons-material/Group';

export interface PlanListItemData {
  id: string;
  goal: string;
  summary: string;
  status: PlanStatus;
  createdAt: string;
  updatedAt: string;
  accessLevel: ParticipantAccessLevel;
  isOwner: boolean;
  accessLabel: string;
  visibleStepCount: number;
  totalStepCount: number;
  progressPercent: number;
}

interface PlanListRowProps {
  plan: PlanListItemData;
}

export const PlanListRow: React.FC<PlanListRowProps> = ({ plan }) => {
  const navigate = useNavigate();

  const getStatusColor = (status: PlanStatus): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
      case PlanStatus.Draft:
        return 'default';
      case PlanStatus.Active:
        return 'primary';
      case PlanStatus.Completed:
        return 'success';
      case PlanStatus.Archived:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getAccessIcon = () => {
    if (plan.isOwner) {
      return <PersonIcon sx={{ fontSize: 14 }} />;
    }
    if (plan.accessLevel === 'ADMIN') {
      return <AdminPanelSettingsIcon sx={{ fontSize: 14 }} />;
    }
    return <GroupIcon sx={{ fontSize: 14 }} />;
  };

  const getAccessChipColor = (): 'default' | 'primary' | 'secondary' | 'info' => {
    if (plan.isOwner) {
      return 'primary';
    }
    if (plan.accessLevel === 'ADMIN') {
      return 'secondary';
    }
    return 'info';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Box
      onClick={() => navigate(`/app/plans/${plan.id}`)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      {/* Status indicator */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: `${getStatusColor(plan.status)}.main`,
          mt: 1,
          flexShrink: 0,
        }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title row with status and access */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 100,
            }}
          >
            {plan.goal}
          </Typography>
          <Chip
            label={plan.status}
            size="small"
            color={getStatusColor(plan.status)}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Chip
            icon={getAccessIcon()}
            label={plan.accessLabel}
            size="small"
            color={getAccessChipColor()}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        {/* Progress bar */}
        {plan.visibleStepCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={plan.progressPercent}
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                bgcolor: 'action.hover',
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
              {Math.round(plan.progressPercent)}%
            </Typography>
          </Box>
        )}

        {/* Metadata */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Step count - show visible vs total for non-owners */}
          {plan.totalStepCount > 0 && (
            <Typography variant="caption" color="text.secondary">
              {plan.isOwner || plan.accessLevel === 'ADMIN'
                ? `${plan.totalStepCount} ${plan.totalStepCount === 1 ? 'step' : 'steps'}`
                : `${plan.visibleStepCount}/${plan.totalStepCount} steps visible`}
            </Typography>
          )}

          {/* Updated date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(plan.updatedAt)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PlanListRow;
